/**
 * React Query hooks for issue tickets data fetching and mutations.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  listIssueTickets,
  createIssueTicket,
  syncIssueTicket,
  type CreateTicketData,
} from "@/services/issueTicketsService";
import { useApiReady } from "@/hooks/useApi";

/**
 * Hook to fetch a paginated list of tickets for a security issue.
 * @param issueId - The numeric ID of the issue
 * @param page - The page number (default: 1)
 */
export function useIssueTickets(issueId: number, page: number = 1) {
  const isApiReady = useApiReady();

  return useQuery({
    queryKey: ["issue-tickets", issueId, page],
    queryFn: () => listIssueTickets(issueId, page),
    staleTime: 30000, // 30 seconds
    enabled: issueId > 0 && isApiReady,
  });
}

/**
 * Hook to create a Jira ticket for a security issue.
 * Invalidates the tickets list and activities list on settled.
 */
export function useCreateIssueTicket() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ issueId, data }: { issueId: number; data: CreateTicketData }) =>
      createIssueTicket(issueId, data),
    onSettled: (data, error, variables) => {
      if (error) {
        toast.error("Failed to create ticket", {
          description: error instanceof Error ? error.message : undefined,
        });
      } else if (data) {
        toast.success("Ticket created", {
          description: `${data.external_ticket_id} created in Jira`,
        });
      }
      // Invalidate tickets for this issue
      queryClient.invalidateQueries({ queryKey: ["issue-tickets", variables.issueId] });
      // Invalidate activities so the History tab picks up the new TICKET_CREATED entry
      queryClient.invalidateQueries({ queryKey: ["issue-activities"] });
    },
  });
}

/**
 * Hook to sync (refresh) a ticket's external status from Jira.
 * Invalidates the tickets list on settled.
 */
export function useSyncIssueTicket() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ ticketId }: { ticketId: number; issueId: number }) =>
      syncIssueTicket(ticketId),
    onSettled: (data, error, variables) => {
      if (error) {
        toast.error("Failed to sync ticket", {
          description: error instanceof Error ? error.message : undefined,
        });
      } else if (data) {
        toast.success("Ticket synced", {
          description: data.external_status
            ? `Status: ${data.external_status}`
            : "Status updated",
        });
      }
      // Invalidate tickets for this issue
      queryClient.invalidateQueries({ queryKey: ["issue-tickets", variables.issueId] });
    },
  });
}
